import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, CardHeader, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Menu, MoreHorizontal } from "lucide-react";
import MenuBar from "./MenuBar";
import ModalEditableCell from "./ModalEditableCell";
import { Checkbox } from "@mui/material";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import { useToast } from "./ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";

interface AggregatedAmount {
  currentAmount: string;
  wtd: string;
  mtd: string;
  ytd: string;
}

interface AggregatedHours {
  currentAmount: string;
  wtd: string;
  mtd: string;
  ytd: string;
  currentRate?: string;
}

interface EmployeeEarning {
  earningCode: string;
  displayName: string;
  amount: AggregatedAmount;
  hours: AggregatedHours;
}

interface EmployeeDeduction {
  deductionCode: string;
  displayName: string;
  employeeAmount: string;
  employerAmount: string;
  employeePercentage: string;
  employerPercentage: string;
  isEEAmountEditable: boolean;
  isERAmountEditable: boolean;
  isEEPercentEditable: boolean;
  isERPercentEditable: boolean;
}

interface Employee {
  id: number;
  plivId: number;
  firstName: string;
  lastName: string;
  earnings: EmployeeEarning[];
  deductions: EmployeeDeduction[];
}

interface TaxTreatment {
  taxCode: string;
  isTaxable: boolean;
  taxFreeThreshold?: number;
}

interface Earning {
  countryCode: string;
  displayName: string;
  internalEarningCode: string;
  taxTreatment: TaxTreatment;
  customRateId?: number;
}

interface Deduction {
  countryCode: string;
  displayName: string;
  internalDeductionCode: string;
  taxTreatments: any;
  employeeDeduction?: EmployeeDeduction;
}

type EarningOverrideType = "amount" | "hours" | "rate";
type DeductionOverrideType =
  | "employeeAmount"
  | "employerAmount"
  | "employeePercentage"
  | "employerPercentage";

const RunPayroll = () => {
  const { payrunId } = useParams<{ payrunId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("earnings");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [deductions, setDeductions] = useState<Deduction[]>([]);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Extract userId from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const urlUserId = urlParams.get("userId");

  // If URL has userId, use it; otherwise use "current"
  const [selectedUserId, setSelectedUserId] = useState<string>(
    urlUserId || "current",
  );
  const isSuperUserAccount = isSuperUser(user);

  console.log("URL userId param:", urlUserId);
  console.log("Using selectedUserId:", selectedUserId);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);

    // Update URL to reflect the selected user without a full page reload
    const newUrl = new URL(window.location.href);
    if (userId !== "current") {
      newUrl.searchParams.set("userId", userId);
    } else {
      newUrl.searchParams.delete("userId");
    }
    window.history.replaceState({}, "", newUrl.toString());

    // Reload data with the new userId
    fetchEmployeesForPayrun();
    fetchEarningsForPayrun();
    fetchDeductionsForPayrun();
  };

  useEffect(() => {
    fetchEmployeesForPayrun();
    fetchEarningsForPayrun();
    fetchDeductionsForPayrun();
  }, [payrunId, selectedUserId]);

  const fetchEarningsForPayrun = async () => {
    try {
      if (!payrunId) {
        throw new Error("No payrun ID provided");
      }

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<any>(
        EndpointKeys.FETCH_PAYRUN_EARNINGS,
        {
          urlParams: { id: payrunId },
          queryParams,
        },
      );
      setEarnings(data.earnings);
    } catch (error) {
      console.error("Error fetching earnings:", error);
      handleApiError(error);
    }
  };

  const fetchDeductionsForPayrun = async () => {
    try {
      if (!payrunId) {
        throw new Error("No payrun ID provided");
      }

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<any>(
        EndpointKeys.FETCH_PAYRUN_DEDUCTIONS,
        {
          urlParams: { id: payrunId },
          queryParams,
        },
      );
      setDeductions(data.deductions);
    } catch (error) {
      console.error("Error fetching deductions:", error);
      handleApiError(error);
    }
  };

  const fetchEmployeesForPayrun = async () => {
    try {
      if (!payrunId) {
        throw new Error("No payrun ID provided");
      }

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<any>(
        EndpointKeys.FETCH_PAYRUN_EMPLOYEES,
        {
          urlParams: { id: payrunId },
          queryParams,
        },
      );
      setEmployees(data.employees);
    } catch (error) {
      console.error("Error fetching employees:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    // Check if this is an unauthorized or forbidden error
    if (error?.status === 403 || error?.status === 401) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You don't have permission to access this payroll data.",
      });
      navigate("/dashboard");
    }
  };

  const handleEarningOverride = async (
    employee: Employee,
    earningCode: string,
    value: string,
    earningRateType: EarningOverrideType,
  ) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.CREATE_EARNING_OVERRIDE, {
        method: "POST",
        body: {
          payrunLineItemVersion: employee.plivId,
          earningCode,
          [earningRateType]: value,
        },
        queryParams,
      });
      await fetchEmployeesForPayrun();
    } catch (error) {
      console.error("Error updating field:", error);
      throw error;
    }
  };

  const handleEarningOverrideRemoval = async (
    employee: Employee,
    earningCode: string,
    type: EarningOverrideType,
  ): Promise<string> => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.REMOVE_EARNING_OVERRIDE, {
        method: "POST",
        body: {
          payrunLineItemVersion: employee.plivId,
          earningCode,
          type,
        },
        queryParams,
      });
      await fetchEmployeesForPayrun();
      return "0.00";
    } catch (error) {
      console.error("Error removing earning override:", error);
      throw error;
    }
  };

  const handleDeductionOverride = async (
    employee: Employee,
    deductionCode: string,
    value: string,
    overrideType: DeductionOverrideType,
  ) => {
    // Only superusers can create deduction overrides
    if (!isSuperUserAccount) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "Only administrators can modify deduction values.",
      });
      return;
    }

    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.CREATE_DEDUCTION_OVERRIDE, {
        method: "POST",
        body: {
          payrunLineItemVersion: employee.plivId,
          deductionCode,
          [overrideType]: value,
        },
        queryParams,
      });
      await fetchEmployeesForPayrun();
    } catch (error) {
      console.error("Error updating deduction:", error);
      throw error;
    }
  };

  const handleDeductionOverrideRemoval = async (
    employee: Employee,
    deductionCode: string,
    type: DeductionOverrideType,
  ): Promise<string> => {
    // Only superusers can remove deduction overrides
    if (!isSuperUserAccount) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "Only administrators can modify deduction values.",
      });
      return "0.00";
    }

    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.REMOVE_DEDUCTION_OVERRIDE, {
        method: "POST",
        body: {
          payrunLineItemVersion: employee.plivId,
          deductionCode,
          type,
        },
        queryParams,
      });
      await fetchEmployeesForPayrun();
      return "0.00";
    } catch (error) {
      console.error("Error removing deduction override:", error);
      throw error;
    }
  };

  const handleRefreshPayrun = async () => {
    try {
      if (!payrunId) {
        throw new Error("No payrun ID provided");
      }

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.REFRESH_PAYRUN, {
        method: "POST",
        urlParams: { id: payrunId },
        queryParams,
      });
      await fetchEmployeesForPayrun();
    } catch (error) {
      console.error("Error refreshing payrun:", error);
    }
  };

  const handleProcessPayroll = () => {
    // Preserve the current userId parameter in the URL when navigating
    let targetUrl = `/payroll-approval/${payrunId}`;

    // Only add userId param if we're not using the current user
    if (selectedUserId !== "current") {
      targetUrl += `?userId=${encodeURIComponent(selectedUserId)}`;
    }

    // If any employees are explicitly selected, pass them to preview as a CSV list
    if (selected.length > 0) {
      const sep = targetUrl.includes("?") ? "&" : "?";
      targetUrl += `${sep}selected=${encodeURIComponent(selected.join(","))}`;
    }

    console.log("Navigating to:", targetUrl);
    navigate(targetUrl);
  };

  // Compute visible employees (active only if flag provided by backend)
  const visibleEmployees = React.useMemo(() => {
    return employees.filter((e: any) => e?.isActive !== false);
  }, [employees]);

  return (
    <div className="flex min-h-screen bg-background overflow-hidden">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 min-h-screen bg-gray-900 z-20 overflow-y-auto`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col w-full md:w-auto h-screen mt-16 md:mt-0 overflow-hidden">
        <div className="flex-1 px-4 md:px-6 py-4 md:py-6 bg-gray-100 overflow-y-auto">
          <div className="p-4 md:p-6 max-w-7xl space-y-6">
            {isSuperUserAccount && (
              <div className="mb-6 flex justify-end">
                <SuperUserEmployerSelector
                  selectedUserId={selectedUserId}
                  onUserChange={handleUserChange}
                />
              </div>
            )}

            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold">
                  Earnings & Deductions Overview
                </h2>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full md:w-auto">
                    <TabsTrigger value="earnings">Earnings</TabsTrigger>
                    <TabsTrigger value="deductions">Deductions</TabsTrigger>
                  </TabsList>

                  <TabsContent value="earnings">
                    <div className="border rounded-md max-h-[600px] overflow-auto">
                      <div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]">
                                <Checkbox
                                  checked={selected.length === employees.length}
                                  onChange={(checked) => {
                                    setSelected(
                                      checked
                                        ? employees.map((e) => e.id.toString())
                                        : [],
                                    );
                                  }}
                                />
                              </TableHead>
                              <TableHead>Name</TableHead>
                              {earnings.map((earning) => (
                                <React.Fragment
                                  key={earning.internalEarningCode}
                                >
                                  <TableHead>
                                    {earning.displayName} Amount
                                  </TableHead>
                                  <TableHead>
                                    {earning.displayName} Hours
                                  </TableHead>
                                  {earning.customRateId && (
                                    <TableHead>
                                      {earning.displayName} Rate
                                    </TableHead>
                                  )}
                                </React.Fragment>
                              ))}
                              <TableHead className="w-[100px]">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {visibleEmployees.map((employee) => (
                              <TableRow key={employee.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selected.includes(
                                      employee.id.toString(),
                                    )}
                                    onChange={(checked) => {
                                      setSelected((ids) =>
                                        checked
                                          ? [...ids, employee.id.toString()]
                                          : ids.filter(
                                              (id) =>
                                                id !== employee.id.toString(),
                                            ),
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {employee.firstName} {employee.lastName}
                                </TableCell>
                                {earnings.map((earning) => {
                                  const employeeEarning =
                                    employee.earnings.find(
                                      (e) =>
                                        e.earningCode ===
                                        earning.internalEarningCode,
                                    );
                                  return (
                                    <React.Fragment
                                      key={earning.internalEarningCode}
                                    >
                                      <ModalEditableCell
                                        initialValue={
                                          employeeEarning?.amount
                                            .currentAmount || "0.00"
                                        }
                                        isDisabled={!!earning.customRateId}
                                        onSave={(value) =>
                                          handleEarningOverride(
                                            employee,
                                            earning.internalEarningCode,
                                            value,
                                            "amount",
                                          )
                                        }
                                        onRemove={() =>
                                          handleEarningOverrideRemoval(
                                            employee,
                                            earning.internalEarningCode,
                                            "amount",
                                          )
                                        }
                                      />
                                      <ModalEditableCell
                                        initialValue={
                                          employeeEarning?.hours
                                            .currentAmount || "0.00"
                                        }
                                        onSave={(value) =>
                                          handleEarningOverride(
                                            employee,
                                            earning.internalEarningCode,
                                            value,
                                            "hours",
                                          )
                                        }
                                        onRemove={() =>
                                          handleEarningOverrideRemoval(
                                            employee,
                                            earning.internalEarningCode,
                                            "hours",
                                          )
                                        }
                                      />
                                      {earning.customRateId && (
                                        <TableCell>
                                          {employeeEarning?.hours.currentRate ||
                                            "0.00"}
                                        </TableCell>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                                <TableCell>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="deductions">
                    <div className="border rounded-md max-h-[600px] overflow-auto">
                      <div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]">
                                <Checkbox
                                   checked={selected.length === visibleEmployees.length}
                                  onChange={(checked) => {
                                    setSelected(
                                      checked
                                         ? visibleEmployees.map((e) => e.id.toString())
                                        : [],
                                    );
                                  }}
                                />
                              </TableHead>
                              <TableHead>Name</TableHead>
                              {deductions.map((deduction) => (
                                <React.Fragment
                                  key={deduction.internalDeductionCode}
                                >
                                  <TableHead>
                                    {deduction.displayName} Employee Amount
                                  </TableHead>
                                  <TableHead>
                                    {deduction.displayName} Employer Amount
                                  </TableHead>
                                  <TableHead>
                                    {deduction.displayName} Employee Percentage
                                  </TableHead>
                                  <TableHead>
                                    {deduction.displayName} Employer Percentage
                                  </TableHead>
                                </React.Fragment>
                              ))}
                              <TableHead className="w-[100px]">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {employees.map((employee) => (
                              <TableRow key={employee.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selected.includes(
                                      employee.id.toString(),
                                    )}
                                    onChange={(checked) => {
                                      setSelected((ids) =>
                                        checked
                                          ? [...ids, employee.id.toString()]
                                          : ids.filter(
                                              (id) =>
                                                id !== employee.id.toString(),
                                            ),
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {employee.firstName} {employee.lastName}
                                </TableCell>
                                {deductions.map((deduction) => {
                                  const employeeDeduction =
                                    employee.deductions.find(
                                      (d) =>
                                        d.deductionCode ===
                                        deduction.internalDeductionCode,
                                    );
                                  return (
                                    <React.Fragment
                                      key={deduction.internalDeductionCode}
                                    >
                                      <ModalEditableCell
                                        initialValue={
                                          employeeDeduction?.employeeAmount ||
                                          "0.00"
                                        }
                                        isDisabled={
                                          !isSuperUserAccount || 
                                          !deduction.employeeDeduction
                                            ?.isEEAmountEditable
                                        }
                                        onSave={(value) =>
                                          handleDeductionOverride(
                                            employee,
                                            deduction.internalDeductionCode,
                                            value,
                                            "employeeAmount",
                                          )
                                        }
                                        onRemove={() =>
                                          handleDeductionOverrideRemoval(
                                            employee,
                                            deduction.internalDeductionCode,
                                            "employeeAmount",
                                          )
                                        }
                                      />
                                      <ModalEditableCell
                                        initialValue={
                                          employeeDeduction?.employerAmount ||
                                          "0.00"
                                        }
                                        isDisabled={
                                          !isSuperUserAccount || 
                                          !deduction.employeeDeduction
                                            ?.isERAmountEditable
                                        }
                                        onSave={(value) =>
                                          handleDeductionOverride(
                                            employee,
                                            deduction.internalDeductionCode,
                                            value,
                                            "employerAmount",
                                          )
                                        }
                                        onRemove={() =>
                                          handleDeductionOverrideRemoval(
                                            employee,
                                            deduction.internalDeductionCode,
                                            "employerAmount",
                                          )
                                        }
                                      />
                                      <ModalEditableCell
                                        initialValue={
                                          employeeDeduction?.employeePercentage ||
                                          "0.00"
                                        }
                                        isDisabled={
                                          !isSuperUserAccount || 
                                          !deduction.employeeDeduction
                                            ?.isEEPercentEditable
                                        }
                                        onSave={(value) =>
                                          handleDeductionOverride(
                                            employee,
                                            deduction.internalDeductionCode,
                                            value,
                                            "employeePercentage",
                                          )
                                        }
                                        onRemove={() =>
                                          handleDeductionOverrideRemoval(
                                            employee,
                                            deduction.internalDeductionCode,
                                            "employeePercentage",
                                          )
                                        }
                                      />
                                      <ModalEditableCell
                                        initialValue={
                                          employeeDeduction?.employerPercentage ||
                                          "0.00"
                                        }
                                        isDisabled={
                                          !isSuperUserAccount || 
                                          !deduction.employeeDeduction
                                            ?.isERPercentEditable
                                        }
                                        onSave={(value) =>
                                          handleDeductionOverride(
                                            employee,
                                            deduction.internalDeductionCode,
                                            value,
                                            "employerPercentage",
                                          )
                                        }
                                        onRemove={() =>
                                          handleDeductionOverrideRemoval(
                                            employee,
                                            deduction.internalDeductionCode,
                                            "employerPercentage",
                                          )
                                        }
                                      />
                                    </React.Fragment>
                                  );
                                })}
                                <TableCell>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex flex-col sm:flex-row sm:justify-end space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                  <Button
                    onClick={handleProcessPayroll}
                    className="w-full sm:w-auto"
                  >
                    Process Payroll
                  </Button>
                  <Button
                    onClick={handleRefreshPayrun}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    Refresh Payrun
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunPayroll;
